/**
 * The permission matrix: one row per area, one tick per capability.
 *
 * Ticking "delete" ticks "edit" and "view" with it, and unticking "view"
 * unticks the rest — because a person who can change something they cannot
 * see is not a state anyone means to create, and leaving it reachable means
 * someone eventually creates it by accident.
 */
import { AREA_DESCRIPTIONS, CAPABILITIES, type Area, type AreaGrant, type Capability, type Grants, type Preset } from "@dp/shared";

const CAPABILITY_LABELS: Record<Capability, string> = {
  view: "See",
  edit: "Change",
  delete: "Remove",
};

/** Apply one tick, keeping the implications straight. */
export function applyTick(grant: AreaGrant, capability: Capability, value: boolean): AreaGrant {
  if (value) {
    if (capability === "delete") return { view: true, edit: true, delete: true };
    if (capability === "edit") return { ...grant, view: true, edit: true };
    return { ...grant, view: true };
  }
  if (capability === "view") return { view: false, edit: false, delete: false };
  if (capability === "edit") return { ...grant, edit: false, delete: false };
  return { ...grant, delete: false };
}

export function PermissionMatrix({
  grants, disabled, isAdmin, presets, onChange,
}: {
  grants: Grants;
  disabled: boolean;
  isAdmin: boolean;
  presets: Preset[];
  onChange: (grants: Grants) => void;
}) {
  function tick(area: Area, capability: Capability, value: boolean) {
    onChange({ ...grants, [area]: applyTick(grants[area], capability, value) });
  }

  return (
    <div className="stack-sm">
      {isAdmin && (
        <div className="notice notice-warn small">
          An administrator may do everything regardless of what is ticked here. These ticks take effect if the
          administrator flag is ever turned off.
        </div>
      )}

      {!disabled && presets.length > 0 && (
        <div className="row presets">
          <span className="small muted">Start from:</span>
          {presets.map((preset) => (
            <button
              key={preset.key} type="button" className="btn btn-sm" title={preset.description}
              onClick={() => onChange(preset.grants as Grants)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      <div className="table-wrap">
        <table className="table matrix">
          <thead>
            <tr>
              <th>Area</th>
              {CAPABILITIES.map((capability) => <th key={capability} className="matrix-tick">{CAPABILITY_LABELS[capability]}</th>)}
            </tr>
          </thead>
          <tbody>
            {AREA_DESCRIPTIONS.map((description) => (
              <tr key={description.area}>
                <td>
                  <div className="row">
                    <strong>{description.label}</strong>
                    <span className="chip">{description.surface === "both" ? "both" : description.surface === "config" ? "studio" : "portal"}</span>
                  </div>
                  <div className="small muted">{description[CAPABILITIES[0]!]}</div>
                </td>
                {CAPABILITIES.map((capability) => (
                  <td key={capability} className="matrix-tick">
                    <label className="checkline" title={description[capability]}>
                      <input
                        type="checkbox"
                        checked={grants[description.area]?.[capability] ?? false}
                        disabled={disabled}
                        onChange={(event) => tick(description.area, capability, event.target.checked)}
                      />
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
